<!--
  Day | Week | Month, with the matching date input. The scope lives in the URL
  (?day= / ?week= / ?month=) so a reload or a shared link lands on the same
  figures; this component only reads it and emits navigations.

  Week deliberately uses a DATE input, not type="week": pick any day and the
  Monday-to-Sunday week containing it is the scope (normalised to its Monday
  before it reaches the URL). type="week" renders as a bare text box on
  iOS Safari and Firefox, and an ISO week number means nothing to someone
  standing on a site — "the week with the 3rd in it" does.

  Look: one control, not two — a navy segmented switch welded to a pill date
  field, sized to sit inside whatever screen embeds it. type="month" gets no
  styling from app.css, so the inputs are dressed here to match.
-->
<script lang="ts">
  import { weekStart } from '../../lib/calc/week';
  import { location, navigate } from '../../lib/router';
  import type { PnlScopeQuery } from '../../lib/router';
  import { dayFor, dayForWeek, readScope, rememberDay, todayIso } from './scope';

  interface Props {
    /** Builds the path for a new scope — the screen decides where it lives. */
    pathFor: (scope: PnlScopeQuery) => string;
  }
  let { pathFor }: Props = $props();

  const scope = $derived(readScope($location.query));
  const mode = $derived(scope.month !== undefined ? 'month' : scope.week !== undefined ? 'week' : 'day');

  // Every day actually viewed is remembered, so switching to Week or Month
  // and back returns here — not to the Monday or the 1st.
  $effect(() => {
    if (scope.day) rememberDay(scope.day);
  });

  function go(next: PnlScopeQuery) {
    navigate(pathFor(next), { replace: true, keepScroll: true });
  }

  /** The day this scope is anchored on, for switching between the modes. */
  function anchorDay(): string {
    if (scope.day) return scope.day;
    if (scope.week) return dayForWeek(scope.week);
    if (scope.month) return dayFor(scope.month);
    return todayIso();
  }

  function setDay(value: string) {
    if (value) go({ day: value });
  }
  function setWeek(value: string) {
    const monday = value ? weekStart(value) : null;
    if (monday) go({ week: monday });
  }
  function setMonth(value: string) {
    if (value) go({ month: value });
  }
  function toDay() {
    go({ day: anchorDay() });
  }
  function toWeek() {
    go({ week: weekStart(anchorDay()) ?? todayIso() });
  }
  function toMonth() {
    go({ month: anchorDay().slice(0, 7) });
  }
</script>

<div class="scope">
  <div class="scope__switch" role="tablist">
    <button type="button" aria-pressed={mode === 'day'} onclick={toDay}>Day</button>
    <button type="button" aria-pressed={mode === 'week'} onclick={toWeek}>Week</button>
    <button type="button" aria-pressed={mode === 'month'} onclick={toMonth}>Month</button>
  </div>
  <label class="scope__field">
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
    {#if mode === 'month'}
      <input type="month" value={scope.month} onchange={(e) => setMonth((e.currentTarget as HTMLInputElement).value)} aria-label="Month" />
    {:else if mode === 'week'}
      <!-- The week's Monday; picking any other day lands on ITS week. -->
      <input type="date" value={scope.week} onchange={(e) => setWeek((e.currentTarget as HTMLInputElement).value)} aria-label="Week (pick any day in it)" />
    {:else}
      <input type="date" value={scope.day} onchange={(e) => setDay((e.currentTarget as HTMLInputElement).value)} aria-label="Day" />
    {/if}
  </label>
</div>

<style>
  .scope {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
  }

  .scope__switch {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    flex: 0 0 auto;
    /* Three equal thirds now; sized so "Month" still fits its third. */
    width: 14.5rem;
    border-radius: 999px;
    background: var(--bg-sunken);
  }
  /* Each half is a whole tap target, so the pill is the full height of the
     track rather than an inset that a gloved thumb can miss. */
  .scope__switch button {
    min-height: var(--tap);
    padding: 0 0.5rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    font: inherit;
    font-size: 0.88rem;
    font-weight: 700;
    color: var(--ink-soft);
    cursor: pointer;
    touch-action: manipulation;
    transition: background 150ms ease-out, color 150ms ease-out;
  }
  .scope__switch button:hover {
    color: var(--ink);
  }
  .scope__switch button[aria-pressed='true'] {
    background: var(--brand);
    color: #fff;
    box-shadow: var(--shadow);
  }
  .scope__switch button:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }

  .scope__field {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    flex: 1 1 9rem;
    min-width: 0;
    max-width: 14rem;
    min-height: var(--tap);
    padding: 0 0.75rem;
    border: 1.5px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
  }
  .scope__field:focus-within {
    border-color: var(--brand);
    box-shadow: 0 0 0 3px var(--brand-tint);
  }
  .scope__field svg {
    width: 1.05rem;
    height: 1.05rem;
    flex: 0 0 auto;
    fill: none;
    stroke: var(--ink-faint);
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .scope__field input {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    min-height: calc(var(--tap) - 4px);
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--ink);
    appearance: none;
  }
  /* The ring belongs to the pill, not to the input sitting inside it. */
  .scope__field input:focus-visible {
    outline: none;
    border-color: transparent;
  }
  .scope__field input::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.65;
  }

  @media (prefers-reduced-motion: reduce) {
    .scope__switch button {
      transition: none;
    }
  }
</style>
